import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { cn } from '@elara/client-core/lib/utils';
import { currentGuide } from '@elara/client-core/states/guide-stack';

import { sentry } from '#lib/sentry.js';
import { useRouterState, useTripsState } from '#states/hooks.js';
import { DestinationGuide } from '#ui/guides/destination-guide.js';
import { FlightGuide } from '#ui/guides/flight-guide.js';
import { GuideHeaderActionsSlot } from '#ui/guides/guide-header-actions.js';
import { GuideScope } from '#ui/guides/guide-scope.js';
import { HotelGuide } from '#ui/guides/hotel-guide.js';
import { PlaceGuide } from '#ui/guides/place-guide.js';
import { RoomGuide } from '#ui/guides/room-guide.js';
import { Button, Loader, Logo, MaterialIcon } from '#ui/index.js';

import { GuideBreadcrumbs } from './GuideBreadcrumbs.js';

// Transition timing constants matching Tailwind classes: delay-300 duration-300
const TRANSITION_DELAY_MS = 300;
const TRANSITION_DURATION_MS = 300;
const TRANSITION_BUFFER_MS = 50;
const FALLBACK_TIMEOUT_MS = TRANSITION_DELAY_MS + TRANSITION_DURATION_MS + TRANSITION_BUFFER_MS;
/** How long a returned-to guide may take to grow tall enough to scroll back to where it was left. */
const SCROLL_RESTORE_TIMEOUT_MS = 1000;
/** Past this scroll offset the header gains its border and the guide's title. */
const HEADER_SCROLL_THRESHOLD = 40;

/**
 * Puts each guide back where it was left when Back or Forward returns to it. A guide rebuilds on
 * return and often shows a loader first, so the saved offset can be out of reach for a moment: the
 * restore waits for the content to grow (or the traveller to scroll, or a second to pass), and
 * offsets are not recorded meanwhile, so a scroll the browser clamps during the swap cannot
 * overwrite one.
 */
function useScrollPerGuide(
  container: React.RefObject<HTMLDivElement | null>,
  guideKey: string,
  onRestored: (top: number) => void,
) {
  const offsets = useRef(new Map<string, number>());
  const restoring = useRef(false);

  useLayoutEffect(() => {
    const element = container.current;
    if (!element) return;
    const target = offsets.current.get(guideKey) ?? 0;
    element.scrollTop = target;
    onRestored(element.scrollTop);
    if (element.scrollTop >= target) return;

    restoring.current = true;
    const finish = () => {
      restoring.current = false;
      observer.disconnect();
      clearTimeout(timer);
      element.removeEventListener('wheel', finish);
      element.removeEventListener('touchstart', finish);
      element.removeEventListener('keydown', finish);
      onRestored(element.scrollTop);
    };
    const observer = new ResizeObserver(() => {
      element.scrollTop = target;
      if (element.scrollTop >= target) finish();
    });
    for (const child of [...element.children]) observer.observe(child);
    const timer = setTimeout(finish, SCROLL_RESTORE_TIMEOUT_MS);
    element.addEventListener('wheel', finish, { passive: true });
    element.addEventListener('touchstart', finish, { passive: true });
    element.addEventListener('keydown', finish);
    return finish;
    // `onRestored` is a state setter wrapper; the restore follows the guide, not its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideKey]);

  return (top: number) => {
    if (!restoring.current) offsets.current.set(guideKey, top);
  };
}

/**
 * Preview panel component - handles transition state and renders content after transition completes
 */
export function PreviewPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }): React.ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isOpen);
  const tripsState = useTripsState();
  const routerState = useRouterState();
  const [isTransitionComplete, setIsTransitionComplete] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  // The floating header's slot for the guide's actions, once mounted.
  const [headerActionsSlot, setHeaderActionsSlot] = useState<HTMLDivElement | null>(null);

  // The guides walked through in this panel: Back/Forward and the breadcrumb move within them.
  const stack = routerState.guideStack;
  const guideKey = currentGuide(stack)?.key ?? 'preview';
  // The breadcrumb lists the guides before this one; guides ahead (after Back) are reached with Forward.
  const showsBreadcrumbs = !!stack && stack.index > 0;
  const recordScroll = useScrollPerGuide(scrollContainerRef, guideKey, setScrollPosition);

  // First check if previewData exists (button-triggered)
  const previewData = tripsState.previewData;

  // Determine preview type and ID from previewData or hash params
  const previewType = previewData?.type;

  // Reset transition state when panel opens/closes
  useEffect(() => {
    // Keep ref in sync with current isOpen value to avoid stale closures
    isOpenRef.current = isOpen;
    setIsFullWidth(false);

    if (!isOpen) {
      // When closing, immediately hide content to avoid stale content
      setIsTransitionComplete(false);
      return;
    }

    // When opening, wait for transition to complete
    setIsTransitionComplete(false);

    const panel = panelRef.current;
    if (!panel) return;

    const handleTransitionEnd = (event: TransitionEvent) => {
      // Only handle opacity transitions on the panel element itself, not children
      // The opacity transition is what controls visibility, so we wait for it to complete
      if (event.target === panel && event.propertyName === 'opacity') {
        // Check if we're in the open state (opacity-100) to confirm transition completed
        // Use ref to get the current value, not the stale closure value
        const computedStyle = window.getComputedStyle(panel);
        const opacity = computedStyle.opacity;
        if (opacity === '1' && isOpenRef.current) {
          setIsTransitionComplete(true);
        }
      }
    };

    panel.addEventListener('transitionend', handleTransitionEnd);

    // Fallback: if transitionend doesn't fire (shouldn't happen, but safety net)
    // Use ref to get the current value, not the stale closure value
    const fallbackTimer = setTimeout(() => {
      if (isOpenRef.current) {
        setIsTransitionComplete(true);
      }
    }, FALLBACK_TIMEOUT_MS);

    return () => {
      // Use the captured panel reference to ensure we remove the listener from the same element
      // Add null check to handle edge cases where the element might be null during cleanup
      if (panel) {
        panel.removeEventListener('transitionend', handleTransitionEnd);
      }
      clearTimeout(fallbackTimer);
    };
  }, [isOpen]);

  return (
    <div
      className={cn(
        'absolute z-40 bottom-0 right-0 h-full flex gap-4 flex-shrink-0 transition-all duration-300 ease-in-out w-full md:max-w-[50%]',
        !isOpen && 'w-0',
        isFullWidth && 'md:max-w-full',
      )}
    >
      <div
        ref={panelRef}
        className={cn(
          'relative h-full w-full bg-background shadow-sm overflow-hidden',
          'transition-all ease-in-out',
          isFullWidth ? 'rounded-none' : 'md:rounded-tl-lg',
          isOpen
            ? 'opacity-100 translate-x-0 delay-300 duration-300'
            : 'opacity-0 translate-x-8 pointer-events-none duration-150',
        )}
      >
        <div
          className={cn(
            'absolute px-5 md:px-8 top-0 left-0 right-0 z-10 bg-background/80 backdrop-blur-md flex justify-center',
            scrollPosition > HEADER_SCROLL_THRESHOLD && 'border-b',
          )}
        >
          <div className="max-w-6xl w-full flex items-center gap-1 py-1">
            {/* The way back through the guides walked in this panel — the same moves as the browser's
                Back and Forward, which go through the same history. Shown at every width: it is the
                panel's only way back. */}
            {showsBreadcrumbs && (
              <GuideBreadcrumbs
                stack={stack}
                onSelect={(index) => routerState.goToGuide(index)}
                narrow={tripsState.isMobile}
              />
            )}

            {/* The guide's own title, faded in once its heading has scrolled away — after the
                breadcrumb's trailing slash, as the last step of the path. */}
            {tripsState.previewPanelTitle && (
              <div
                className={cn(
                  'min-w-0 truncate text-sm font-medium opacity-0 transition-opacity duration-300 ease-in-out',
                  // No margin of its own: the header's `gap-1` sets it off from the breadcrumb's trailing slash
                  // by the same 4px as the slash from the crumb, and it sits flush left when it leads.
                  scrollPosition > HEADER_SCROLL_THRESHOLD && 'opacity-100',
                )}
              >
                {tripsState.previewPanelTitle}
              </div>
            )}

            {/* The panel's own controls, together at the end: fill the screen (from `md` up, where the panel
                is narrower than it), then close. */}
            <div className="-mr-2.5 ml-auto flex shrink-0 items-center gap-1">
              {/* The guide's own actions, icon-only, once its header has scrolled away with them (the guide
                  renders them here: `GuideLayout`). Hidden until then, out of reach of clicks and Tab. */}
              <div
                ref={setHeaderActionsSlot}
                className={cn(
                  'flex items-center gap-1 transition-[opacity,visibility] duration-300 ease-in-out',
                  scrollPosition > HEADER_SCROLL_THRESHOLD ? 'visible opacity-100' : 'invisible opacity-0',
                )}
              />
              <Button
                variant="icon"
                size="icon-sm"
                onClick={() => setIsFullWidth(!isFullWidth)}
                tooltip={isFullWidth ? 'Shrink' : 'Fill screen'}
                aria-label={isFullWidth ? 'Shrink' : 'Fill screen'}
                className="hidden md:flex"
              >
                <MaterialIcon icon={isFullWidth ? 'right_panel_close' : 'fullscreen'} />
              </Button>
              <Button variant="icon" size="icon-sm" onClick={onClose} tooltip="Close" aria-label="Close">
                <MaterialIcon icon="close" />
              </Button>
            </div>
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          className="h-full w-full flex flex-col overflow-y-auto overflow-x-hidden p-4 md:p-8 pt-12 md:pt-12"
          onScroll={(event) => {
            setScrollPosition(event.currentTarget.scrollTop);
            recordScroll(event.currentTarget.scrollTop);
          }}
        >
          {/* A column, so a guide can grow to the panel's height and centre what it shows when empty. */}
          <div className="flex flex-1 flex-col">
            {/* Show loader while waiting for transition to complete */}
            {isOpen && !isTransitionComplete ? (
              <div className="flex items-center justify-center h-full">
                <Loader loading={true} />
              </div>
            ) : (
              /* Only render content after transition completes when opening */
              isTransitionComplete &&
              isOpen && (
                <GuideScope>
                  <GuideHeaderActionsSlot.Provider value={headerActionsSlot}>
                    {/* Keyed by guide: each one in the stack mounts fresh, and a guide that failed does
                      not leave the next one showing its error. */}
                    <ErrorBoundary
                      FallbackComponent={PreviewErrorFallback}
                      resetKeys={[guideKey]}
                      onError={(error, errorInfo) => {
                        console.error('Preview content error:', error, errorInfo);
                        sentry?.captureException(error);
                      }}
                    >
                      <PreviewContent key={guideKey} />
                    </ErrorBoundary>
                  </GuideHeaderActionsSlot.Provider>
                </GuideScope>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Error fallback component for preview content errors
 */
function PreviewErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
      <div className="mx-auto max-w-sm text-left">
        <Logo variant="symbol" width={24} />
        <h1 className="mt-4 text-2xl font-semibold font-display">Something went wrong</h1>
        <p className="mt-2 mb-6 text-muted-foreground">
          We encountered an error while loading this content. This might be a temporary issue.
        </p>

        {process.env.NODE_ENV === 'development' && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Error Details (Development)
            </summary>
            <pre className="mt-2 rounded bg-muted p-3 text-xs text-muted-foreground overflow-auto">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button onClick={resetErrorBoundary}>Try again</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Preview content component - displays the preview content based on previewData or hash params
 */
export function PreviewContent(): React.ReactNode {
  const tripsState = useTripsState();
  const routerState = useRouterState();
  const hashParams = routerState.getHashParams();

  // First check if previewData exists (button-triggered)
  const previewData = tripsState.previewData;

  // Determine preview type and ID from previewData or hash params (room is always by id from hash)
  const previewType = previewData?.type ?? hashParams.preview;
  const previewId = hashParams.id;

  if (!previewType) {
    return (
      <div className="flex flex-col items-center text-center">
        <MaterialIcon icon="preview" className="text-4xl text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No content to preview</p>
      </div>
    );
  }

  // Room preview: load by roomOptionId from hash (showGuide('room', id))
  if (previewType === 'room' && previewId) {
    return <RoomGuide roomOptionId={previewId} />;
  }

  // Render preview based on type
  if (previewType === 'destination') {
    if (!previewId) {
      return (
        <div className="flex flex-col items-center text-center">
          <MaterialIcon icon="preview" className="text-4xl text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No destination ID provided</p>
        </div>
      );
    }
    return <DestinationGuide destinationId={previewId} />;
  }

  if (previewType === 'hotel') {
    return <HotelGuide hotelId={previewId} />;
  }

  if (previewType === 'place') {
    return <PlaceGuide placeId={previewId} />;
  }

  if (previewType === 'flight' && previewId) {
    return <FlightGuide flightOptionId={previewId} />;
  }

  // Unknown preview type
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
      <div className="mx-auto max-w-sm text-left">
        <Logo variant="symbol" width={24} />
        <h1 className="mt-4 text-2xl font-semibold font-display">We couldn’t find what you were looking for</h1>
        <p className="mt-2 mb-6 text-muted-foreground">
          We encountered an error while loading this content. This might be a temporary issue.
        </p>
      </div>
    </div>
  );
}
